(function () {
  const MAX_INPUT_BYTES = 10 * 1024 * 1024;
  const MAX_OUTPUT_BYTES = 2 * 1024 * 1024;
  const MAX_DIMENSION = 2048;
  const ACCEPTED_EXTENSIONS = /\.(jpe?g|png|webp|heic|heif)$/i;

  const isAcceptedImage = (file) => {
    const type = String(file.type || '').toLowerCase();
    return type.startsWith('image/') || ACCEPTED_EXTENSIONS.test(file.name || '');
  };

  const loadWithImageElement = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve({
      width: image.naturalWidth,
      height: image.naturalHeight,
      draw: (context, width, height) => context.drawImage(image, 0, 0, width, height),
      close: () => URL.revokeObjectURL(url)
    });
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('unsupported-image'));
    };
    image.src = url;
  });

  const loadImage = async (file) => {
    if ('createImageBitmap' in window) {
      try {
        let bitmap;
        try {
          bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (error) {
          bitmap = await createImageBitmap(file);
        }
        return {
          width: bitmap.width,
          height: bitmap.height,
          draw: (context, width, height) => context.drawImage(bitmap, 0, 0, width, height),
          close: () => bitmap.close()
        };
      } catch (error) {
        return loadWithImageElement(file);
      }
    }
    return loadWithImageElement(file);
  };

  const dataUrlBytes = (value) => Math.ceil((value.length - value.indexOf(',') - 1) * 0.75);

  const prepare = async (file, options = {}) => {
    if (!file || !isAcceptedImage(file)) throw new Error('unsupported-image');
    if (file.size > (options.maxInputBytes || MAX_INPUT_BYTES)) throw new Error('photo-too-large');

    const source = await loadImage(file);
    const maximum = Math.min(options.maxDimension || MAX_DIMENSION, MAX_DIMENSION);
    const targetBytes = Math.min(options.maxDataBytes || MAX_OUTPUT_BYTES, MAX_OUTPUT_BYTES);
    const dimensions = [maximum, 1800, 1600, 1400, 1200, 1000, 800]
      .filter((value, index, values) => value <= maximum && values.indexOf(value) === index);
    const qualities = [0.86, 0.74, 0.62, 0.5];
    let result = '';

    try {
      for (const maxDimension of dimensions) {
        const ratio = Math.min(1, maxDimension / Math.max(source.width, source.height));
        const width = Math.max(1, Math.round(source.width * ratio));
        const height = Math.max(1, Math.round(source.height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { alpha: false });
        if (!context) throw new Error('image-processing-failed');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
        source.draw(context, width, height);

        for (const quality of qualities) {
          result = canvas.toDataURL('image/jpeg', quality);
          if (result && result !== 'data:,' && dataUrlBytes(result) <= targetBytes) return result;
        }
      }
    } finally {
      source.close();
    }

    if (result && result !== 'data:,') throw new Error('photo-output-too-large');
    throw new Error('image-processing-failed');
  };

  window.FurAndFarImage = { prepare, isAcceptedImage };
}());
